import React, { Fragment } from 'react';
import { FormattedMessage, intlShape } from 'react-intl';
import { PropTypes } from 'prop-types';
import moment from 'moment';
import { map, groupBy, pipe, values, filter } from 'ramda';
import { sum, multiply } from 'utils/calculator';
import { directions, ANY_BELGIAN_STATION_SHORT_TARIFF_CODE, ADS_THROUGH_BRUSSELS_SHORT_TARIFF_CODE } from 'constants/booking';
import { legShape, productsShape } from 'constants/propTypes';
import { mapObject } from 'utils/functions';
import { Column } from 'components/shared/Layout';
import LoyaltyPointsInfos from 'components/shared/LoyaltyPointsInfos';
import FormattedCurrency from 'components/ui/FormattedCurrency';
import InventoryClassMessage from 'pages/BookingDetails/AfterSalesSummaryBox/InventoryClassMessage';
import RebookMessage from 'pages/BookingDetails/AfterSalesSummaryBox/RebookMessage';
import RefundMessage from 'pages/BookingDetails/AfterSalesSummaryBox/RefundMessage';
import currencies from 'constants/currencies';
import { passengerTypes } from 'types/passengers';
import { isNoMealClass, isNoMealRoute } from 'utils/meals';
import { isInfantsOnLapEnabled } from 'utils/featureFlags';
import {
  getPaxDetails,
  getPaxSummary as getPaxInfoSummary,
  overrideCompanionFaresForABSADSAccessibleTravels
} from 'components/shared/JourneyDigest/JourneyDigest.utils';

import productTypes from '../../../../../../constants/productTypes';
import { mergePassengersByType } from '../../../BookingPathReviewBookingSummary.utils';
import {
  countPaxTypes,
  getPaxSummary,
  groupMeals,
  handleTotal,
  handleTotalPoints,
  handlePaxInfo,
  groupObjectsInArrayByKeys
} from '../../SegmentSummary.utils';

import PaxInfo from './PaxInfo';
import { InfoLabel } from './Info/Info.styles';
import {
  StyledFareCondition,
  TravelDate,
  FareConditionsContainer,
  InfoGrid,
  InfoGridHeaderCell,
  InfoGridCell,
  InfantParentInfo
} from './SegmentInfo.styles';

const determineAddOnLegJourneyId = (isPsuedo) => (isPsuedo ? 'journeySummary.validBeforeJourney' : 'journeySummary.validAfterJourney');

const SegmentInfo = ({
  isAddOnLeg = false,
  direction,
  leg: {
    origin,
    destination,
    classOfService,
    trainNumber,
    bookingItems = [],
    meals = [],
    timing: { departs, arrives, isoDate },
    extraProducts = [],
    products,
    seats,
    serviceType = {}
  },
  intl: { formatMessage },
  productFamilyName,
  productFamilies,
  currency,
  groupName = '',
  isMiceGroupBooking,
  infantPassengers = [],
  isLoyaltyBooking = false
}) => {
  const infantsOnLapEnabled = isInfantsOnLapEnabled();
  const visibleInfantPassengers = infantsOnLapEnabled ? infantPassengers : [];
  const passengers = bookingItems.map((item) => item.passenger);
  const paxDetails = products && getPaxDetails(passengers, products);
  const groupPaxInfosByType = groupObjectsInArrayByKeys(Object.values(getPaxInfoSummary(paxDetails)), 'type');
  const paxSummary = getPaxSummary(passengers, bookingItems);
  const paxCount = countPaxTypes(passengers);
  const total = handleTotal(paxSummary);
  const totalPoints = handleTotalPoints(paxSummary);
  const firstBookingItem = bookingItems[0];
  const canLookupMessage = firstBookingItem && firstBookingItem.canBeCancelled && firstBookingItem.canBeRefunded;
  const canBeCancelled = firstBookingItem && firstBookingItem?.canBeCancelled;
  const paxInfo = paxCount && handlePaxInfo(productFamilyName, paxCount, products, productFamilies);
  const fareInfos = paxInfo && paxInfo[0];
  const paxsByType = mergePassengersByType(bookingItems);
  const groupedMeals = meals.length > 0 && groupMeals(meals);

  const combineDuplicateProductsWithQuantity = (p) => ({
    product: p[0],
    quantity: p.filter((product) => !product.cancelled).length
  });

  const specialProductsByPassengers = pipe(
    filter((i) => i.type === productTypes.DISABILITY_SUPPORT),
    filter((i) => !i.cancelled),
    groupBy((p) => `${p.code}-${p.price}-${p.currency}`),
    values(),
    map(combineDuplicateProductsWithQuantity),
    map((p) => ({ product: p.product, quantity: p.quantity }))
  )(extraProducts);

  const extraProductsByPassengers = pipe(
    filter((i) => ![productTypes.DISABILITY_SUPPORT, productTypes.FOOD_AND_BEVERAGES].includes(i.type)),
    filter((i) => !i.cancelled),
    filter(
      (epp) =>
        epp?.tariffDetails?.tariffCode?.slice(0, 3) !== ANY_BELGIAN_STATION_SHORT_TARIFF_CODE &&
        epp?.tariffDetails?.tariffCode?.slice(0, 3) !== ADS_THROUGH_BRUSSELS_SHORT_TARIFF_CODE
    ),
    groupBy((p) => `${p.code}-${p.price}-${p.currency}`),
    values(),
    map(combineDuplicateProductsWithQuantity),
    map((p) => ({ product: p.product, quantity: p.quantity }))
  )(extraProducts);

  const extrasTotal = isAddOnLeg
    ? 0
    : extraProductsByPassengers.reduce((acc, { product: { price }, quantity }) => sum([acc, multiply(price, quantity)]), 0);

  const departValue = isAddOnLeg
    ? `${origin?.name}: ${formatMessage({
        id: determineAddOnLegJourneyId(origin?.isPseudoStation)
      })}`
    : `${origin?.name} (${origin?.shortCode}) ${formatMessage({
        id: 'exchangeConfirmation.at'
      })} ${departs}`;

  const arriveValue = isAddOnLeg
    ? destination?.name
    : `${destination?.name} (${destination?.shortCode}) ${formatMessage({
        id: 'exchangeConfirmation.at'
      })} ${arrives}`;

  const getCoach = (pax) => {
    const seat = seats?.find((s) => s.passenger?.id === pax.id);

    return seat?.coach;
  };

  const getSeat = (pax) => {
    const seat = seats?.find((s) => s.passenger?.id === pax.id);

    return seat?.number;
  };

  const hasSeatingInfo = () => {
    const seatsFiltered = seats?.filter(
      (s) => (passengers.map((p) => p.id).includes(s.passenger?.id) && s.coach !== null) || s.number !== null
    );

    return seatsFiltered?.length > 0;
  };

  const overrideAccesibleTravelPaxTypes = overrideCompanionFaresForABSADSAccessibleTravels(paxDetails, isAddOnLeg);
  const shouldShowMeals =
    !isAddOnLeg && groupedMeals && !isNoMealClass(classOfService.code) && !isNoMealRoute(origin?.uic, destination?.uic);

  return (
    <>
      <Column>
        <TravelDate>{moment.utc(isoDate).format('Do MMM YYYY')}</TravelDate>
      </Column>
      <InfoGrid>
        <InfoGridHeaderCell>
          <FormattedMessage id="segmentInfo.depart" />:
        </InfoGridHeaderCell>
        <InfoGridCell data-testid="depart" column="span 3">
          {departValue}
        </InfoGridCell>
        <InfoGridHeaderCell>
          <FormattedMessage id="segmentInfo.arrive" />:
        </InfoGridHeaderCell>
        <InfoGridCell data-testid="arrives" column="span 3">
          {arriveValue}
        </InfoGridCell>
        <InfoGridHeaderCell>
          <FormattedMessage id="segmentInfo.class" />:
        </InfoGridHeaderCell>
        <InfoGridCell data-testid="class" column="span 3">
          {classOfService.name}
        </InfoGridCell>
        {!isAddOnLeg && (
          <>
            <InfoGridHeaderCell>
              <FormattedMessage id="segmentInfo.trainNo" />:
            </InfoGridHeaderCell>
            <InfoGridCell data-testid="trainNo" column="span 3">
              {!!serviceType.code && `${serviceType.code} `}
              {trainNumber}
            </InfoGridCell>
          </>
        )}
        {passengers && (
          <>
            <InfoGridHeaderCell>
              <FormattedMessage id="segmentInfo.pax" />:
            </InfoGridHeaderCell>
            {groupName !== '' ? (
              <InfoGridCell column="span 3" data-pax-id={`group--${groupName}`} data-cy={`group--${groupName}`} key={`group--${groupName}`}>
                {groupName}
              </InfoGridCell>
            ) : (
              <>
                <InfoGridHeaderCell>
                  <FormattedMessage id="reviewScreen.passenger" />
                </InfoGridHeaderCell>
                <InfoGridHeaderCell>{hasSeatingInfo() && <FormattedMessage id="reviewScreen.coach" />}</InfoGridHeaderCell>
                <InfoGridHeaderCell>{hasSeatingInfo() && <FormattedMessage id="reviewScreen.seat" />}</InfoGridHeaderCell>
                {passengers.map((pax) => {
                  const infantOnLap = visibleInfantPassengers.find((inf) => inf.passengerRef === pax.id);
                  return (
                    <Fragment key={pax.id}>
                      <InfoGridCell />
                      <InfoGridCell data-pax-id={pax.id} data-cy={pax.id}>
                        {pax.firstName} {pax.lastName}
                      </InfoGridCell>
                      <InfoGridCell data-pax-coach={pax.id}>{hasSeatingInfo() && getCoach(pax)}</InfoGridCell>
                      <InfoGridCell data-pax-seat={pax.id}>{hasSeatingInfo() && getSeat(pax)}</InfoGridCell>
                      {infantOnLap && (
                        <>
                          <InfoGridCell />
                          <InfoGridCell data-cy={`infant-${pax.id}`}>
                            {infantOnLap.firstName} {infantOnLap.lastName}
                            <InfantParentInfo>
                              <FormattedMessage
                                id="summary.infantSittingOnLap"
                                values={{ passengerName: `${pax.firstName} ${pax.lastName}` }}
                              />
                            </InfantParentInfo>
                          </InfoGridCell>
                          <InfoGridCell data-cy={`infant-coach-${pax.id}`}>-</InfoGridCell>
                          <InfoGridCell data-cy={`infant-seat-${pax.id}`}>-</InfoGridCell>
                        </>
                      )}
                    </Fragment>
                  );
                })}
              </>
            )}
          </>
        )}
        {fareInfos && (
          <>
            <InfoGridHeaderCell>
              <FormattedMessage id={fareInfos.label} />:
            </InfoGridHeaderCell>
            <InfoGridCell data-cy={fareInfos.dataId} data-testid={fareInfos.dataId} column="span 3">
              {fareInfos.value}
              {isMiceGroupBooking ? ' (Mice Group)' : ''}
            </InfoGridCell>
          </>
        )}
        <PaxInfo
          paxDetails={groupPaxInfosByType}
          currency={firstBookingItem.currency}
          isABSOrAdsAccessibleTravel={overrideAccesibleTravelPaxTypes}
        />
        {visibleInfantPassengers.length > 0 && (
          <>
            <InfoGridCell />
            <InfoGridCell data-cy="infant-tariff-count">{infantPassengers.length}</InfoGridCell>
            <InfoGridCell>
              <FormattedMessage id="summary.infantOnLap" />
            </InfoGridCell>
            <InfoGridCell data-cy="infant-tariff-price">
              <FormattedCurrency currency={firstBookingItem.currency} amount={0} />
              {isLoyaltyBooking && <LoyaltyPointsInfos dataId="points-details" id="searchResults.pts" values={0} />}
            </InfoGridCell>
          </>
        )}
        {shouldShowMeals && (
          <>
            <InfoGridHeaderCell data-cy="meals">
              <FormattedMessage id="segmentInfo.meals" />:
            </InfoGridHeaderCell>
            <InfoGridHeaderCell>
              <FormattedMessage id="paymentsSummary.amount" />
            </InfoGridHeaderCell>
            <InfoGridHeaderCell>
              <FormattedMessage id="reviewScreen.option" />
            </InfoGridHeaderCell>
            <InfoGridHeaderCell>
              <FormattedMessage id="reviewScreen.pricePerUnit" />
            </InfoGridHeaderCell>
            {mapObject(
              (key, num) => (
                <Fragment key={key}>
                  <InfoGridCell />
                  <InfoGridCell data-cy={`meals-${key}-${num}`}>{num}</InfoGridCell>
                  <InfoGridCell data-cy={`meals-${key}`}>{key}</InfoGridCell>
                  <InfoGridCell>
                    {currencies[firstBookingItem?.currency]}
                    {0}
                  </InfoGridCell>
                </Fragment>
              ),
              groupedMeals
            )}
          </>
        )}
        {!isAddOnLeg && specialProductsByPassengers.length > 0 && (
          <>
            <InfoGridHeaderCell>
              <FormattedMessage id="assistance.assistance" />:
            </InfoGridHeaderCell>
            <InfoGridHeaderCell>
              <FormattedMessage id="paymentsSummary.amount" />
            </InfoGridHeaderCell>
            <InfoGridHeaderCell>
              <FormattedMessage id="reviewScreen.item" />
            </InfoGridHeaderCell>
            <InfoGridHeaderCell>
              <FormattedMessage id="reviewScreen.pricePerUnit" />
            </InfoGridHeaderCell>
            {specialProductsByPassengers.map(({ product: { name }, quantity }) => (
              <Fragment key={name}>
                <InfoGridCell />
                <InfoGridCell data-cy={`assistance-${name}-${quantity}`}>{quantity}</InfoGridCell>
                <InfoGridCell data-cy={`assistance-${name}`}>{name}</InfoGridCell>
                <InfoGridCell>
                  {currencies[firstBookingItem.currency]}
                  {0}
                </InfoGridCell>
              </Fragment>
            ))}
          </>
        )}
        {!isAddOnLeg && extraProductsByPassengers.length > 0 && (
          <>
            <InfoGridHeaderCell>
              <FormattedMessage id="segmentInfo.extras" />:
            </InfoGridHeaderCell>
            <InfoGridHeaderCell>
              <FormattedMessage id="paymentsSummary.amount" />
            </InfoGridHeaderCell>
            <InfoGridHeaderCell>
              <FormattedMessage id="reviewScreen.item" />
            </InfoGridHeaderCell>
            <InfoGridHeaderCell>
              <FormattedMessage id="reviewScreen.pricePerUnit" />
            </InfoGridHeaderCell>
            {extraProductsByPassengers.map(({ product: { name, price }, quantity }) => (
              <Fragment key={name}>
                <InfoGridCell />
                <InfoGridCell>{quantity}</InfoGridCell>
                <InfoGridCell>{name}</InfoGridCell>
                <InfoGridCell>
                  {currencies[currency]}
                  {price}
                </InfoGridCell>
              </Fragment>
            ))}
          </>
        )}
        <InfoGridHeaderCell>
          <FormattedMessage id="journeySummary.total" values={{ direction }} />:
        </InfoGridHeaderCell>
        <InfoGridCell data-cy={`total--${direction}`} data-testid={`price-total--${direction}`} column="span 3">
          <FormattedCurrency currency={firstBookingItem?.currency} amount={sum([total, extrasTotal])} />
          {!!totalPoints && <LoyaltyPointsInfos dataId="points-details" id="searchResults.pts" values={totalPoints} />}
        </InfoGridCell>
      </InfoGrid>

      {paxsByType && (
        <FareConditionsContainer>
          {paxsByType.map((item) => (
            <div key={item.passenger.id} data-cy={`fare-container--${item.passenger.id}`}>
              <Column>
                <InfoLabel>
                  <FormattedMessage id="segmentSummary.fareConditions" />{' '}
                  <FormattedMessage id={`passengerTypes.${passengerTypes[item.passenger.type]}`} />:
                </InfoLabel>
              </Column>

              <StyledFareCondition>
                <ul>
                  <li>
                    <InventoryClassMessage name={item.name} classOfService={classOfService?.name} />
                  </li>
                  <li>
                    <RebookMessage
                      canBeRebooked={item.canBeRebooked}
                      afterSalesRules={item.afterSalesRules}
                      timezone={origin?.timezone}
                      currency={item.currency}
                    />
                  </li>
                  <li>
                    <RefundMessage
                      canLookupRefundMessage={canLookupMessage}
                      afterSalesRules={item.afterSalesRules}
                      timezone={origin?.timezone}
                      currency={currency}
                      canBeCancelled={canBeCancelled}
                    />
                  </li>
                </ul>
              </StyledFareCondition>
            </div>
          ))}
        </FareConditionsContainer>
      )}
    </>
  );
};

SegmentInfo.propTypes = {
  isAddOnLeg: PropTypes.bool,
  leg: legShape,
  productFamilyName: PropTypes.string,
  intl: intlShape,
  currency: PropTypes.string,
  direction: PropTypes.oneOf(directions).isRequired,
  groupName: PropTypes.string,
  productFamilies: productsShape,
  isMiceGroupBooking: PropTypes.bool,
  infantPassengers: PropTypes.arrayOf(
    PropTypes.shape({
      firstName: PropTypes.string,
      lastName: PropTypes.string,
      passengerRef: PropTypes.string
    })
  ),
  isLoyaltyBooking: PropTypes.bool
};

export default SegmentInfo;
